import React, { useState, useImperativeHandle, forwardRef } from "react";
import { Modal, Button, Row, Col, Badge, Table } from "react-bootstrap";
import FeatherIcon from "feather-icons-react";

export interface OrderViewModalRef {
  open: (order: any) => void;
  close: () => void;
}

export const OrderViewModal = forwardRef<OrderViewModalRef, {}>((_, ref) => {
  const [show, setShow] = useState(false);
  const [order, setOrder] = useState<any | null>(null);

  useImperativeHandle(ref, () => ({
    open: (orderData) => {
      setOrder(orderData);
      setShow(true);
    },
    close: handleClose,
  }));

  const handleClose = () => {
    setShow(false);
    setOrder(null);
  };

  const orderStatuses = ["Pending", "Confirmed", "Completed", "Cancelled"];
  const orderColors = ["warning text-dark", "info", "success", "danger"];

  const paymentStatuses = ["Unpaid", "Paid"];
  const paymentColors = ["warning text-dark", "success"];

  return (
    <>
      {/* 1. MATCHED BACKDROP OVERLAY - Fixes background masking depth */}
      {show && (
        <div
          onClick={handleClose}
          style={{
            position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.4)", zIndex: 1040, backdropFilter: "blur(1px)"
          }}
        />
      )}

      {/* 2. MATCHED MODAL WITH POSITION WRAPPERS - Forces centering alignment over data layout tables */}
      <Modal
        show={show}
        onHide={handleClose}
        centered
        size="lg"
        container={document.body}
        style={{
          position: "fixed", top: "50%", left: "50%",
          transform: show ? "translate(-50%, -50%)" : "translate(-50%, -60%)",
          zIndex: 1050, display: show ? "block" : "none", width: "100%", maxWidth: "800px"
        }}
      >
        <div className="modal-content border-0 shadow-lg bg-white rounded-3 overflow-hidden">
          <Modal.Header className="bg-light border-bottom px-4 py-3 d-flex justify-content-between align-items-center">
            <Modal.Title className="h5 fw-bold mb-0 text-dark d-flex align-items-center">
              <FeatherIcon icon="shopping-bag" size={18} className="me-2 text-primary" />
              Order Management Details #ORD-{order?.id || ""}
            </Modal.Title>
            <button 
              type="button" 
              className="btn-close shadow-none m-0 p-2" 
              onClick={handleClose} 
              aria-label="Close" 
            />
          </Modal.Header>

          <Modal.Body className="p-4" style={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}>
            {order ? (
              <>
                {/* Section 1: Customer Profile vs Status Summary */}
                <Row className="g-4 text-start mb-4">
                  <Col md={6} className="border-end border-light">
                    <h5 className="text-uppercase tracking-wider text-primary fw-bold small mb-3">Customer Profile</h5>
                    
                    <div className="mb-3">
                      <small className="text-muted d-block data-field-label">Customer Name</small>
                      <span className="fw-bold text-dark fs-6">{order.customer?.name || "Guest Checkout"}</span>
                    </div>

                    <div className="mb-3">
                    <small className="text-muted d-block data-field-label">Mobile Number</small>
                    <span className="text-dark fw-medium">{order.customer?.mobile_no || "N/A"}</span>
                  </div>

                  <div className="mb-0">
                    <small className="text-muted d-block data-field-label">Email Address</small>
                    <span className="text-muted small">{order.customer?.email || "N/A"}</span>
                  </div>
                </Col>

                <Col md={6}>
                  <h5 className="text-uppercase tracking-wider text-primary fw-bold small mb-3">Order Status & Metrics</h5>
                  
                  <Row className="mb-2">
                    <Col xs={6} className="mb-2">
                      <small className="text-muted d-block data-field-label">Order Status</small>
                      <Badge bg={orderColors[order.statuses?.order_status] || 'secondary'} className="text-uppercase px-2 py-1">
                        {orderStatuses[order.statuses?.order_status] || "Unknown"}
                      </Badge>
                    </Col>
                    <Col xs={6} className="mb-2">
                      <small className="text-muted d-block data-field-label">Payment Status</small>
                      <Badge bg={paymentColors[order.statuses?.payment_status] || 'secondary'} className="text-uppercase px-2 py-1">
                        {paymentStatuses[order.statuses?.payment_status] || "Unpaid"}
                      </Badge>
                    </Col>
                  </Row>

                  <div className="mb-2">
                    <small className="text-muted d-block data-field-label">Payment Method</small>
                    <span className="text-dark fw-semibold text-uppercase small">{order.statuses?.payment_method || "COD"}</span>
                  </div>

                  <div className="mb-0">
                    <small className="text-muted d-block data-field-label">Booking Reference Link</small>
                    <Badge bg="dark" className="font-monospace px-2 py-1">
                      BK-{order.booking_id || "Direct Entry"}
                    </Badge>
                  </div>
                </Col>
              </Row>

              <hr className="border-light my-4" />

              {/* Section 2: Logistics Delivery Destination */}
              <div className="text-start mb-4 p-3 bg-light border rounded">
                <h5 className="text-uppercase tracking-wider text-primary fw-bold small mb-2">Logistics Destination</h5>
                <small className="text-muted d-block data-field-label">Location Environment ({order.logistics?.location_type || "Center"})</small>
                <p className="mb-0 text-dark small fw-medium">{order.logistics?.address || "Served directly inside business context hub."}</p>
              </div>

              {/* Section 3: Ordered Items Specification Summary */}
              <div className="text-start mb-0">
                <h5 className="text-uppercase tracking-wider text-primary fw-bold small mb-3">Items Purchased</h5>
                
                {order.order_items && order.order_items.length > 0 ? (
                  <Table responsive bordered hover className="align-middle bg-white rounded mb-0">
                    <thead className="table-light small text-uppercase">
                      <tr>
                        <th>Item Code / Title</th>
                        <th className="text-center">Quantity</th>
                        <th className="text-end">Unit Price</th>
                        <th className="text-end">Item Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.order_items.map((item: any) => (
                        <tr key={item.id} className="small">
                          <td className="fw-bold text-dark">
                            Asset Item Identification Ref #{item.business_item_id}
                          </td>
                          <td className="text-center fw-bold">{item.quantity}</td>
                          <td className="text-end">{parseFloat(item.unit_price || 0).toFixed(2)} BDT</td>
                          <td className="text-end fw-bold text-primary">
                            {parseFloat(item.total || 0).toFixed(2)} BDT
                          </td>
                        </tr>
                      ))}
                      
                      {/* Financial Summary Calculation Stack */}
                      <tr className="table-light small">
                        <td colSpan={3} className="text-end fw-semibold">Subtotal:</td>
                        <td className="text-end">{parseFloat(order.pricing?.subtotal || 0).toFixed(2)} BDT</td>
                      </tr>
                      {parseFloat(order.pricing?.coupon_amount || 0) > 0 && (
                        <tr className="table-light small text-danger">
                          <td colSpan={3} className="text-end fw-semibold">Coupon discount applied:</td>
                          <td className="text-end">- {parseFloat(order.pricing.coupon_amount).toFixed(2)} BDT</td>
                        </tr>
                      )}
                      <tr className="table-light fw-bold fs-6">
                        <td colSpan={3} className="text-end text-uppercase small">Grand Total Amount Payable:</td>
                        <td className="text-end text-primary">
                          {parseFloat(order.pricing?.grand_total || 0).toFixed(2)} BDT
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                ) : (
                  <div className="p-3 bg-light rounded text-muted small text-center fw-medium">
                    No catalog service items linked to this specific order layout.
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-4 text-muted">Parsing order data snapshot model...</div>
          )}
        </Modal.Body>

        <Modal.Footer className="border-top px-4 py-3 bg-light mb-0 d-flex justify-content-end">
          <Button variant="outline-secondary" className="px-4 fw-bold" onClick={handleClose}>
            Dismiss View
          </Button>
        </Modal.Footer>
      </div>

      <style>{`
        .data-field-label {
          font-size: 0.72rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 3px;
        }
      `}</style>
    </Modal>
   </>
  );
});

OrderViewModal.displayName = "OrderViewModal";